import PropTypes from 'prop-types';
import { action, extendObservable } from 'mobx';
import FlagStore from '../FlagStore';
import AsyncActionStore from '../AsyncActionStore';
import BoolStore from '../helpers/BoolStore';
import PolicyModalHeaderStore from './PolicyModalHeaderStore';

export const PolicyModalSectionProps = {
    isDefault: PropTypes.bool,
    isEmpty: PropTypes.func,
    setValue: PropTypes.func.isRequired,
    getValue: PropTypes.func.isRequired,
    hasChanged: PropTypes.func.isRequired,
};

export const ModalMode = {
    EDIT: 'edit',
    VIEW: 'view',
    CREATE: 'add',
    DELETE: 'delete',
    EDIT_CONFIRM: 'edit_confirm'
};

export default class PolicyModalStore {
    static PropType = PropTypes.shape({
        header: PolicyModalHeaderStore.PropType,
        noHeader: PropTypes.bool,

        modal: FlagStore.PropType.isRequired,
        mode: PropTypes.string,
        requireEditConfirmation: PropTypes.func,
        actionSpinnerEnabled: BoolStore.PropTypes,

        [ModalMode.CREATE]: PropTypes.func.isRequired,
        [ModalMode.EDIT]: PropTypes.func.isRequired,
        [ModalMode.DELETE]: PropTypes.func.isRequired,
        [ModalMode.EDIT_CONFIRM]: PropTypes.func.isRequired,
        onComplete: PropTypes.func.isRequired,
        preprocessHeaderValue: PropTypes.func,

        deleteConfirmation: FlagStore.PropType,
        editConfirmation: FlagStore.PropType,
        cancelEditConfirmation: FlagStore.PropType,

        ...PolicyModalSectionProps,
    });

    constructor(data = {}) {
        extendObservable(this, /** @class PolicyModalStore */{
            header: new PolicyModalHeaderStore({
                noStatusField: data.noStatusField
            }),
            noHeader: false,
            subTitle: null,
            preprocessHeaderValue: null,

            sections: {},
            originalValue: null,

            modal: new FlagStore(),
            mode: null,

            onComplete: () => null,

            deleteConfirmation: new FlagStore(false),
            editConfirmation: new FlagStore(false),
            cancelEditConfirmation: new FlagStore(false),

            status: new AsyncActionStore(),

            latestRevision: null,
            actionSpinnerDisabled: new BoolStore(false),
            preprocessBeforeSubmit: null,
        }, data);
        this[ModalMode.EDIT_CONFIRM] = this[ModalMode.EDIT_CONFIRM] || this[ModalMode.EDIT];
    }

    get isValid() {
        return (this.noHeader || this.header.isValid) && Object.values(this.sections)
            .reduce((acc, section) => {
                const s = section && section.dataPrefix ? section.store : section;

                return acc &&
                    (
                        (typeof s.isValid === 'undefined') ||
                        (typeof s.isValid === 'function' && s.isValid()) ||
                        (typeof s.isValid === 'boolean' && s.isValid)
                    );
            }, true);
    }

    setMode = action((mode) => {
        this.mode = mode;
    });

    resetToDefaults = action(() => {
        this.deleteConfirmation.off();
        this.editConfirmation.off();
        this.originalValue = null;
        this.lastChangeAuthor = null;

        if (!this.noHeader) {
            this.header.resetToDefaults();
        }

        const reset = (store) => (store.resetToDefault ? store.resetToDefault() : store.setValue(null, true));
        Object.values(this.sections)
            .forEach((section) => reset(section.dataPrefix ? section.store : section));
    });

    setValue = action((value) => {
        this.deleteConfirmation.off();
        this.editConfirmation.off();
        this.originalValue = value;

        if (!this.noHeader) {
            this.header.setValue(value);
        } else if (this.preprocessHeaderValue !== null) {
            this.subTitle = this.preprocessHeaderValue(value);
        }
        Object.keys(this.sections)
            .forEach((sectionKey) => {
                const section = this.sections[sectionKey];
                if (section.dataPrefix) {
                    // Second param is whether or not to commit the value, without it EntityStore / BoolStore etc
                    // would treat this change as making the store dirty
                    section.store.setValue(value && value[section.dataPrefix], true);
                } else {
                    section.setValue(value, true);
                }
            });
    });

    getValue = () => ({
        ...this.originalValue,
        ...this.noHeader ? {} : this.header.getValue(),
        ...Object.keys(this.sections)
            .reduce((acc, sectionKey) => {
                const section = this.sections[sectionKey];
                const sectionValue = section.dataPrefix ? {
                    [section.dataPrefix]: section.store.getValue()
                } : section.getValue();
                return ({ ...acc, ...sectionValue });
            }, {})

    });

    hasChanged = () => (this.noHeader ? false : this.header.hasChanged()) ||
        Object.keys(this.sections)
            .some((sectionKey) => {
                const section = this.sections[sectionKey];
                return section.dataPrefix ? section.store.hasChanged() : section.hasChanged();
            });

    complete = () => {
        if (!this.status.isLoading) {
            const item = this.getValue();
            if (this.preprocessBeforeSubmit) {
                this.preprocessBeforeSubmit(item);
            }
            const completeFn = this[this.mode];
            this.status.setLoading(completeFn(item))
                .then((savedItem) => this.onComplete(this.mode, savedItem));
        }
        return this.status.promise;
    };

    shouldShowEditConfirm = () => {
        return this.mode === ModalMode.EDIT_CONFIRM;
    };

    getSubTitle = () => {
        if (this.subTitle === null) {
            return this.mode === ModalMode.EDIT_CONFIRM ? '- Allowed URLs' : `- ${this.header.name.value || 'Unnamed'}`;
        }
        return this.subTitle.length > 1 ? `- ${this.subTitle}` : '';
    }
}
